import React, { useState } from "react";
import {
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineLocationMarker,
} from "react-icons/hi";

import { apiUrl, brands } from "../config/config";
import SEO from "../components/SEO";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    brand: brands,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    setError("");

    try {
      const res = await fetch(`${apiUrl}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Something went wrong");
      }

      setSuccess("Thank you! Your message has been sent successfully.");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        brand: brands,
      });
    } catch (err) {
      setError(err.message || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Blank Beanies"
        keywords="Blank Beanies"
      />

      <div className="min-h-screen bg-[#f5f6f5] py-16 md:py-20">
        <div className="container mx-auto max-w-6xl px-4">
          {/* Header */}
          <div className="relative mb-16 overflow-hidden rounded-[28px] bg-[#1e242a] px-5 py-14 text-center shadow-[0_24px_70px_rgba(30,36,42,0.18)] sm:px-8 md:rounded-[40px] md:py-20">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1e242a] via-[#37404a] to-[#5f6870]"></div>
            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl"></div>
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#e5e8e6]/10 blur-3xl"></div>

            <div className="relative z-10 mx-auto max-w-4xl">
              <span className="mb-6 inline-flex rounded-full border border-white/15 bg-white/10 px-5 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#e5e8e6] shadow-sm backdrop-blur-sm">
                Contact
              </span>

              <h1 className="text-5xl font-extrabold leading-tight text-white md:text-6xl">
                Contact Us
              </h1>

              <p className="mx-auto mt-4 max-w-3xl text-lg leading-relaxed text-white/85">
                Have questions or need assistance? Reach out to our team at{" "}
                <span className="font-semibold text-[#e5e8e6]">
                  Personalised Hat Patches
                </span>.
              </p>
            </div>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:gap-12">
            {/* Contact Info */}
            <div className="relative overflow-hidden rounded-3xl bg-[#1e242a] p-8 shadow-[0_20px_55px_rgba(30,36,42,0.16)] sm:p-10">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1e242a] via-[#37404a] to-[#5f6870]"></div>
              <div className="pointer-events-none absolute -right-20 -bottom-20 h-64 w-64 rounded-full bg-[#e5e8e6]/10 blur-3xl"></div>

              <div className="relative z-10">
                <h2 className="text-3xl font-bold text-white">
                  Get in Touch
                </h2>

                <div className="mt-8 space-y-5 text-white/85">
                  <p className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/10 p-4 text-sm shadow-sm backdrop-blur-sm sm:text-base">
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[#e5e8e6] text-[#1e242a]">
                      <HiOutlineMail className="h-6 w-6" />
                    </span>
                    support@personalisedhatpatches.com
                  </p>

                  <p className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/10 p-4 text-sm shadow-sm backdrop-blur-sm sm:text-base">
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-[#e5e8e6] text-[#1e242a]">
                      <HiOutlinePhone className="h-6 w-6" />
                    </span>
                    (703) 666-8193
                  </p>

                  {/* <p className="flex items-center gap-3">
                    <HiOutlineLocationMarker className="text-blue-500 w-6 h-6" />
                    Los Angeles, CA
                  </p> */}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-3xl border border-[#1e242a]/10 bg-white p-8 shadow-[0_20px_55px_rgba(30,36,42,0.10)] sm:p-10">
              <h2 className="mb-6 text-3xl font-bold text-[#1e242a]">
                Send Us a Message
              </h2>

              {success && (
                <div className="mb-4 rounded-2xl border border-[#1e242a]/10 bg-[#e5e8e6] p-3 text-[#1e242a]">
                  {success}
                </div>
              )}

              {error && (
                <div className="mb-4 rounded-2xl bg-red-100 p-3 text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {["name", "email", "subject"].map((field) => (
                  <div key={field}>
                    <label className="mb-2 block font-semibold capitalize text-[#1e242a]">
                      {field}
                    </label>
                    <input
                      type={field === "email" ? "email" : "text"}
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-[#1e242a]/15 bg-[#f5f6f5] px-5 py-3 text-[#1e242a] outline-none transition-all duration-300 placeholder:text-[#5f6870] focus:border-[#37404a] focus:bg-white focus:ring-2 focus:ring-[#37404a]/25"
                    />
                  </div>
                ))}

                <div>
                  <label className="mb-2 block font-semibold text-[#1e242a]">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full resize-none rounded-2xl border border-[#1e242a]/15 bg-[#f5f6f5] px-5 py-3 text-[#1e242a] outline-none transition-all duration-300 placeholder:text-[#5f6870] focus:border-[#37404a] focus:bg-white focus:ring-2 focus:ring-[#37404a]/25"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-2xl bg-[#1e242a] py-3 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#37404a] disabled:opacity-50"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;